import moment from 'moment';
import { ActionTypes, ProgramTypes } from '../ChannelArchitecture.d';
import { Constants } from '../helper/constants';

// Syncfusion's TimePickerComponent (OpTimeInput) requires a Date value,
// not a bare 'HH:mm:ss' string.
function toTimeInputValue(time) {
  if (!time) return null;
  return moment(time, 'HH:mm:ss').toDate();
}

/**
 * Determines the selected day of the week object based on the block's dayOfWeek and modal type.
 *
 * @param {number|string} dayOfWeek - The block's day-of-week id (1 = Monday ... 7 = Sunday).
 * @param {string} modalType - The current modal action (e.g., ActionTypes.CREATE).
 * @returns {Object|undefined} The day object from Constants.daysOfWeek (e.g., { name: 'Monday', ... }) or undefined if not found.
 */
const getSelectedDayOfWeek = (dayOfWeek, modalType) => {
  const { daysOfWeek: daysOfWeekList } = Constants;

  // New blocks always default to Monday of the current week.
  if (modalType === ActionTypes.CREATE) {
    return daysOfWeekList.find((day) => day.id === 1);
  }

  return daysOfWeekList.find((day) => day.id === Number(dayOfWeek));
};

/**
 * Validates and returns the layout input string.
 * Returns '' if the input is not a valid layout ('Sequential' or 'Shuffle').
 *
 * @param {string} input - The layout value (e.g., 'Sequential', 'Shuffle').
 * @returns {'Sequential'|'Shuffle'|''} The valid layout string or empty string.
 */
const displayLayoutInput = (input) => {
  const layout = ['Sequential', 'Shuffle'];
  return layout.includes(input) ? input : '';
};

/**
 * Maps raw block data into the initial form state required for the Edit/Repeat Modal.
 * Handles logic for resolving linked Series, Seasons, and Title Groups, as well
 * as determining which fields should be disabled based on the Program Type.
 *
 * @param {Object} params - The parameter object.
 * @param {Object} params.blockData - The raw data object of the selected block.
 * @param {string} params.modalType - The current action type (e.g., 'REPEAT', 'EDIT').
 * @param {Array<Object>} params.referenceList - List of available block references for linking.
 * @param {Object} params.schedules - The overall schedule object, used to find parent series details for repeated blocks.
 * @param {Array<Object>} params.seriesList - List of available Series for dropdown resolution.
 * @param {Array<Object>} params.titleGroupList - List of available Title Groups for dropdown resolution.
 *
 * @returns {Object|null} A state object populated with form values and 'disable' flags, or null if blockData is missing.
 */
const getEditBlockState = ({
  blockData,
  modalType,
  referenceList,
  schedules,
  seriesList,
  titleGroupList,
}) => {
  if (!blockData || Object.keys(blockData).length === 0) return null;

  const {
    startTime,
    endTime,
    dayOfWeek,
    isSequential,
    link,
    blockReference,
    titleGroupName,
    blockName,
    genre,
    maxCount,
    availableDuration,
    layout,
    seriesName,
    type,
    seasonID,
    seasonName,
  } = blockData;

  const isModalSingleRepeatType = modalType === ActionTypes.REPEAT;
  const isModalMultipleRepeatType = modalType === ActionTypes.MULTIPLEREPEAT;
  const isModalOfRepeatType = isModalSingleRepeatType || isModalMultipleRepeatType;
  const isProgramRepeat = type === ProgramTypes.REPEAT;
  const selectedTitleGroup = titleGroupList?.find(
    (o) => o.name === titleGroupName
  );

  // Find Link References
  // If Repeat modal, look up by BlockName; otherwise by BlockReference
  const getReference = (key) =>
    referenceList?.find((item) => item.blockName === key) || null;
  const repeatLinkReference = getReference(blockName);
  const standardLinkReference = getReference(blockReference);
  const activeLinkRef = (isModalOfRepeatType)
    ? repeatLinkReference
    : link
    ? standardLinkReference
    : null;

  // Find Series
  let targetSeriesName = seriesName;

  // If it's a REPEAT type, we might need to grab the Series Name from the referenced block
  if (isProgramRepeat && standardLinkReference) {
    const referenceBlock = schedules.programmes.find(
      (e) => standardLinkReference.blockName === e.blockName
    );
    if (referenceBlock?.seriesName) {
      targetSeriesName = referenceBlock.seriesName;
    }
  }

  const selectedSeries = seriesList?.find((o) => o.name === targetSeriesName);

  // Find Season
  const selectedSeason =
    selectedSeries && seasonID
      ? selectedSeries.seasons.find((o) => o.id === +seasonID)
      : null;

  // Build Shared Base Values
  // These values are needed for BOTH Standard Edit and Repeat Edit
  const commonValues = {
    // Note: 'type' might be overwritten below for Repeat modal
    type: type === 'TitleGroup' ? ProgramTypes.TITLEGROUP : type,
    weekNumber: 1,
    dayOfWeek: getSelectedDayOfWeek(dayOfWeek, modalType),
    sequential: isSequential,
    series: selectedSeries,
    seasons: selectedSeason,
    seasonName: seasonName || selectedSeason?.name || null,
    genre,
    blockName,
    layout: displayLayoutInput(layout),
    maxCount,
    availableDuration,
    titleGroup: selectedTitleGroup,
    linkReference: activeLinkRef,
  };

  // Repeat Edit Mode
  if (isModalOfRepeatType) {
    return {
      ...commonValues,
      // Overwrites specific to Repeat Mode
      type: ProgramTypes.REPEAT,
      hasLink: true,
      startTime: null,
      endTime: null,

      // UI Booleans
      disableType: true,
      disableSeason: true,
      disableSeries: true,
      disablePlacement: true,
      disableTitleGroup: true,
      disableLinkReference: true,
      disableLinkToggle: true,
      disableEndTime: false,
      disableStartTime: false,
      disableReccurrence: isModalMultipleRepeatType,
    };
  }

  // Standard Edit Mode
  const isTitleGroup =
    type === 'TitleGroup' || type === ProgramTypes.TITLEGROUP;
  const isSeries = type !== ProgramTypes.SERIES;

  return {
    ...commonValues,
    startTime: toTimeInputValue(startTime),
    endTime: toTimeInputValue(endTime),
    hasLink: link,

    // UI Booleans
    disableSeries: isSeries || isProgramRepeat,
    disableSeason: isSeries || isProgramRepeat,
    disableTitleGroup: !isTitleGroup || isProgramRepeat,
    disableType: false,
    disableLinkReference: false,
    disablePlacement: isProgramRepeat,
    disableLinkToggle: false,
    disableEndTime: false,
    disableStartTime: false,
    disableDayOfWeek: !isProgramRepeat,
  };
};

export default getEditBlockState;
